import axios from "axios";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

const WithDrawMoney = () => {
  const [points, setPoints] = useState(null);
  const [withDrawData, setWithDrawData] = useState({});
  const [allowWithdraw, setAllowWithdraw] = useState(false);
  const [verifyLevel, setVerifyLevel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const token = Cookies.get("jwt");
    axios
      .get(`${process.env.REACT_APP_API}/banking/cashoutData`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        setPoints(data.Fullbalance);
        setWithDrawData(data);
        setAllowWithdraw(data.allowWithdraw);
        setVerifyLevel(data.verifyLevel);
      })
      .catch(() => {
        setLoadError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  function confirmWithDraw() {
    const token = Cookies.get("jwt");
    axios
      .post(
        `${process.env.REACT_APP_API}/banking/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        Swal.fire("สำเร็จ", "รอการตรวจสอบภายใน 24 - 48 ชั่วโมง", "success");
      })
      .catch((err) => {
        Swal.fire("ไม่สำเร็จ", `${err.response?.data?.Error ?? "เกิดข้อผิดพลาด"}`, "error");
      });
  }

  const rows = [
    { label: "ค่าคอมมิชชั่นก่อนหักภาษี", value: withDrawData.Fullbalance, color: "text-gray-800" },
    { label: "มูลค่าหัก ณ ที่จ่าย (3%)", value: withDrawData.withholdingtaxcost ? `-${withDrawData.withholdingtaxcost}` : "-", color: "text-red-500" },
    { label: "ค่าธรรมเนียม", value: withDrawData.feesCost, color: "text-gray-800" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex items-center gap-3"
        >
          <button
            onClick={() => window.location.replace("/user/point")}
            className="w-9 h-9 bg-white rounded-xl flex items-center justify-center border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">คอมมิชชั่น</p>
            <h1 className="text-lg font-bold text-gray-900">ถอนเงิน</h1>
          </div>
        </motion.div>

        {/* ── Balance card ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="px-6 py-5 bg-gradient-to-br from-emerald-50 to-white">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">ยอดเงินสะสมปัจจุบัน</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-emerald-600">{Number(points || 0).toLocaleString()}</span>
              <span className="text-xl text-gray-400 font-medium">฿</span>
            </div>
          </div>
        </motion.div>

        {/* ── Breakdown card ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="px-5 py-3.5 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">รายละเอียดการถอน</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            {rows.map((row, i) => (
              <div key={i} className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{row.label}</p>
                <p className={`text-sm font-semibold ${row.color}`}>{row.value} บาท</p>
              </div>
            ))}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800">เงินเข้าสุทธิ</p>
              <p className="text-lg font-bold text-blue-600">{withDrawData.Cashout} บาท</p>
            </div>
          </div>
        </motion.div>

        {/* ── Load error banner ── */}
        {loadError && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
            className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 text-sm text-red-600"
          >
            โหลดข้อมูลไม่สำเร็จ กรุณารีเฟรชหน้าใหม่
          </motion.div>
        )}

        {/* ── Verify level warning ── */}
        {verifyLevel !== null && verifyLevel < 3 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
            className="bg-yellow-50 border border-yellow-200 rounded-2xl px-5 py-4 text-sm text-yellow-700"
          >
            คุณต้องยืนยันตัวตนด้วยบัตรประชาชน และหน้าสมุดบัญชีก่อน จึงจะถอนเงินได้
          </motion.div>
        )}

        {/* ── Confirm button ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
        >
          {isLoading && (
            <button disabled className="w-full py-3.5 bg-gray-100 text-gray-400 text-sm font-semibold rounded-xl cursor-not-allowed">
              กำลังโหลด...
            </button>
          )}
          {!isLoading && allowWithdraw && (
            <button
              onClick={confirmWithDraw}
              className="w-full py-3.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
            >
              ยืนยันการถอนเงิน
            </button>
          )}
          {!isLoading && !allowWithdraw && (
            <button
              onClick={() => Swal.fire("ยอดเงินขั้นต่ำ", "ยอดเงินขั้นต่ำที่ถอนได้ 1,000 บาท", "warning")}
              className="w-full py-3.5 bg-gray-100 text-gray-400 text-sm font-semibold rounded-xl cursor-not-allowed"
            >
              ยืนยันการถอนเงิน (ยอดไม่ถึงขั้นต่ำ 1,000 ฿)
            </button>
          )}
        </motion.div>

        {/* ── Note ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.18, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4"
        >
          <p className="text-xs text-gray-400 leading-relaxed">
            หลังจากยืนยัน ระบบจะดำเนินการตรวจสอบและโอนเงินภายใน 24 – 48 ชั่วโมงทำการ
          </p>
        </motion.div>

      </div>
    </div>
  );
};

export default WithDrawMoney;
